import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { steps } from '@/mocks/home';
import {
  DiscoverFigure, ImplementFigure, ImproveFigure, methodFigureStyles,
} from './MethodFigures';

type Tone = 'light' | 'dark';

interface WireFigureProps {
  tone?: Tone;
  className?: string;
}

function WireCard({ index, title, description, children }: {
  index: string;
  title: string;
  description: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      el.dataset.visible = 'true';
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.dataset.visible = 'true';
        observer.disconnect();
      }
    }, { threshold: 0.28 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <article ref={ref} className="owf-card cursor-target">
      <div className="owf-index">{index}</div>
      <div className="owf-stage">{children}</div>
      <div className="owf-copy">
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
    </article>
  );
}

const figures = [DiscoverFigure, ImplementFigure, ImproveFigure];

const styles = `
.owf-grid{--owf-fg:oklch(var(--foreground-950));--owf-muted:oklch(var(--foreground-600));--owf-soft:oklch(var(--background-200));display:grid;grid-template-columns:repeat(3,minmax(0,1fr))}
.owf-card{min-width:0;padding:32px 32px 32px;border-right:1px solid var(--owf-soft);color:var(--owf-fg);overflow:hidden;position:relative;isolation:isolate;background:transparent}
.owf-card:last-child{border-right:0}
@media(min-width:1024px){.owf-stage{max-height:max(150px,calc(100svh - 620px))}.owf-stage .hl-stage{height:100%}.owf-stage svg{display:block;width:100%;height:100%}}
.owf-index{font:500 11px/1.2 var(--font-label);letter-spacing:.14em;color:var(--owf-muted)}
.owf-stage{width:100%;aspect-ratio:5/4;margin:8px 0 12px}
.owf-copy h3{margin:0 0 6px;font-family:var(--font-heading);font-weight:300;font-size:clamp(22px,2vw,28px);line-height:1.1;letter-spacing:-.02em}
.owf-copy p{margin:0;color:var(--owf-muted);font-family:var(--font-body);font-size:15px;line-height:1.5;max-width:34ch}
@media(max-width:767px){
.owf-grid{grid-template-columns:1fr}
.owf-card{display:grid;grid-template-columns:112px minmax(0,1fr);grid-template-rows:auto 1fr;column-gap:20px;align-items:start;padding:20px 24px;border-right:0;border-bottom:1px solid var(--owf-soft)}
.owf-card:last-child{border-bottom:0}
.owf-stage{grid-column:1;grid-row:1 / span 2;width:112px;margin:0}
.owf-index{grid-column:2;grid-row:1;margin:2px 0 8px}
.owf-copy{grid-column:2;grid-row:2}
.owf-copy h3{font-size:22px}
.owf-copy p{font-size:14px}
}
@media(max-width:359px){.owf-card{grid-template-columns:96px minmax(0,1fr);column-gap:16px;padding:18px 20px}.owf-stage{width:96px}}
${methodFigureStyles}
`;

export default function OperationalWireFigures({ tone = 'light', className = '' }: WireFigureProps) {
  return (
    <div className={`owf-grid owf-${tone} ${className}`}>
      {steps.map((s, i) => {
        const Figure = figures[i % figures.length];
        return (
          <WireCard key={s.index} index={`FIG ${s.index}`} title={s.title} description={s.desc}>
            <Figure />
          </WireCard>
        );
      })}
      <style>{styles}</style>
    </div>
  );
}